//#region imports
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Button } from 'react-bootstrap';

import {
    selectMovementReportData,
    selectMovementReportStatus,
    selectMovementReportError,
    resetMovementReport,
} from 'redux/slices/sla-reports/movement-report';
import MovementPreadvisedPieChart from './movement-preadvised-pie-chart';
import { sanitizeExcelWorkbook } from '_utils/sanitize-spreadsheet-cell';
//#endregion

//#region constants + styling (PRM Assist house style)
const NAVY = '#1B4F8A';
const BLUE = '#2E86C1';
const ROW_VLT = '#EAF4FB';

// Category order mirrors the backend MOVEMENT_PRM_CODES / OCS movement report layout.
const ASSISTANCE_CODE_ORDER = [
    'BLND', 'DEAF', 'DPNA', 'MAAS', 'MEDA', 'PETC', 'WCBD', 'WCHC', 'WCHR',
    'WCHS', 'WCLB', 'WCMP', 'WCHP', 'WELLCHK', 'BDGR', 'LNYD', 'PREBOARDHS',
    'STCR', 'SVAN', 'WCBW', 'PPOC', 'ESAN', 'OXYG',
];

// category-table column for bookings whose assistance field has none of the codes above
const NO_CODE = 'No Code';

const tableStyle = { borderCollapse: 'collapse', width: '100%', fontSize: '12px' };
const thStyle = {
    border: '1px solid #cbd5e1', padding: '8px 10px', textAlign: 'center',
    backgroundColor: NAVY, color: '#ffffff', fontWeight: 700, whiteSpace: 'nowrap',
};
const subThStyle = { ...thStyle, backgroundColor: BLUE };
const tdStyle = { border: '1px solid #cbd5e1', padding: '7px 10px', textAlign: 'center', color: '#1f2937' };
const labelTdStyle = { ...tdStyle, textAlign: 'left', fontWeight: 600, backgroundColor: ROW_VLT };
const totalRowStyle = { fontWeight: 700, backgroundColor: '#E8F5E9' };
const sectionTitleStyle = { fontSize: '15px', fontWeight: 700, color: BLUE, margin: '22px 0 10px' };
//#endregion

//#region data helpers
const countsFromBreakdown = (breakdown = []) =>
    breakdown.reduce((acc, { assistanceCode, passengerCount }) => {
        acc[assistanceCode] = (acc[assistanceCode] || 0) + (passengerCount || 0);
        return acc;
    }, {});

const orderedCodes = (...breakdowns) => {
    const present = new Set();
    breakdowns.forEach((b) => (b || []).forEach(({ assistanceCode }) => present.add(assistanceCode)));
    const ordered = ASSISTANCE_CODE_ORDER.filter((c) => present.has(c));
    const others = [...present].filter((c) => !ASSISTANCE_CODE_ORDER.includes(c)).sort();
    return [...ordered, ...others];
};

const emptyDir = () => ({ preAdvised: 0, nonAdvised: 0, total: 0 });
const terminalRow = (bucket) => ({
    inbound: bucket?.inbound || emptyDir(),
    outbound: bucket?.outbound || emptyDir(),
    total: bucket?.total || 0,
});
//#endregion

const MovementReportPage = () => {
    //#region redux + local state
    const dispatch = useDispatch();
    const data = useSelector(selectMovementReportData);
    const status = useSelector(selectMovementReportStatus);
    const error = useSelector(selectMovementReportError);
    const reportRef = useRef(null);
    const donutRef = useRef(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadingExcel, setDownloadingExcel] = useState(false);

    // clear report data when leaving the page so a stale report never lingers
    useEffect(() => () => { dispatch(resetMovementReport()); }, [dispatch]);
    //#endregion

    const report = data?.dailyReports?.[0] || null;
    const terminalBreakdown = data?.terminalBreakdown || null;

    //#region derived category table
    const categoryTable = useMemo(() => {
        if (!report) return null;
        const outCounts = countsFromBreakdown(report.outbound?.assistanceBreakdown);
        const inCounts = countsFromBreakdown(report.inbound?.assistanceBreakdown);
        const codes = orderedCodes(report.outbound?.assistanceBreakdown, report.inbound?.assistanceBreakdown);
        // the backend skips bookings with no recognised PRM code in the breakdown but still counts them in the
        // direction totals — surface them as their own column so this table reconciles with the summary
        const unclassified = (dir, counts) => Math.max(0, (dir?.total || 0) - Object.values(counts).reduce((s, n) => s + n, 0));
        const inUnclassified = unclassified(report.inbound, inCounts);
        const outUnclassified = unclassified(report.outbound, outCounts);
        if (inUnclassified || outUnclassified) {
            codes.push(NO_CODE);
            inCounts[NO_CODE] = inUnclassified;
            outCounts[NO_CODE] = outUnclassified;
        }
        const rowFor = (counts) => {
            const cells = codes.map((c) => counts[c] || 0);
            return { cells, total: cells.reduce((s, n) => s + n, 0) };
        };
        const inboundRow = rowFor(inCounts);
        const outboundRow = rowFor(outCounts);
        const totalRow = {
            cells: codes.map((c) => (inCounts[c] || 0) + (outCounts[c] || 0)),
            total: inboundRow.total + outboundRow.total,
        };
        return { codes, inboundRow, outboundRow, totalRow };
    }, [report]);
    //#endregion

    //#region derived terminal table
    const terminalRows = useMemo(() => {
        if (!terminalBreakdown) return null;
        const t1 = terminalRow(terminalBreakdown['1']);
        const t2 = terminalRow(terminalBreakdown['2']);
        const unspecified = terminalRow(terminalBreakdown.unspecified);
        const sumDir = (...dirs) => dirs.reduce(
            (acc, d) => ({
                preAdvised: acc.preAdvised + (d.preAdvised || 0),
                nonAdvised: acc.nonAdvised + (d.nonAdvised || 0),
                total: acc.total + (d.total || 0),
            }),
            emptyDir(),
        );
        const total = {
            inbound: sumDir(t1.inbound, t2.inbound, unspecified.inbound),
            outbound: sumDir(t1.outbound, t2.outbound, unspecified.outbound),
            total: t1.total + t2.total + unspecified.total,
        };
        const rows = [
            { label: 'Terminal 1', ...t1 },
            { label: 'Terminal 2', ...t2 },
        ];
        if (unspecified.total > 0) rows.push({ label: 'Unspecified', ...unspecified });
        return { rows, total };
    }, [terminalBreakdown]);
    //#endregion

    //#region download PDF — rasterises the displayed report (multi-page A4)
    const handleDownloadPdf = async () => {
        if (!reportRef.current || !report) return;
        setDownloading(true);
        try {
            const canvas = await html2canvas(reportRef.current, {
                scale: 1.5,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
            });
            const imgData = canvas.toDataURL('image/jpeg', 0.75);
            const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth - 20;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 10;
            pdf.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight, undefined, 'FAST');
            heightLeft -= pageHeight - 20;
            while (heightLeft > 0) {
                position = heightLeft - imgHeight + 10;
                pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 10, position, imgWidth, imgHeight, undefined, 'FAST');
                heightLeft -= pageHeight - 20;
            }
            pdf.save(`Movement-Report-DUB-${data?.startDate}-to-${data?.endDate}.pdf`);
        } catch (err) {
            console.error('Movement report PDF generation failed:', err);
            alert('Failed to generate PDF. Please try again.');
        } finally {
            setDownloading(false);
        }
    };
    //#endregion

    //#region download Excel — donut (as an image) + same three tables as the screen, numeric cells
    const handleDownloadExcel = async () => {
        if (!report) return;
        setDownloadingExcel(true);
        try {
            const Excel = await import('exceljs/dist/exceljs.min.js');
            const workbook = new Excel.Workbook();
            const sheet = workbook.addWorksheet('Movement Report');

            const argb = (hex) => `FF${hex.replace('#', '').toUpperCase()}`;
            const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            const styleCell = (cell, { fill, font, align = 'center', numFmt } = {}) => {
                cell.border = border;
                cell.alignment = { vertical: 'middle', horizontal: align };
                if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(fill) } };
                if (font) cell.font = font;
                if (numFmt) cell.numFmt = numFmt;
            };
            const headerFont = { bold: true, color: { argb: 'FFFFFFFF' } };
            const styleHeaderRow = (row, from, to, fill = NAVY) => {
                for (let c = from; c <= to; c++) styleCell(row.getCell(c), { fill, font: headerFont });
                row.height = 20;
            };
            const styleBodyRow = (row, to, { isTotal = false, pctCols = [] } = {}) => {
                for (let c = 1; c <= to; c++) {
                    styleCell(row.getCell(c), {
                        fill: isTotal ? '#E8F5E9' : c === 1 ? ROW_VLT : undefined,
                        font: isTotal || c === 1 ? { bold: true } : undefined,
                        align: c === 1 ? 'left' : 'center',
                        numFmt: pctCols.includes(c) ? '0.00%' : undefined,
                    });
                }
            };
            const sectionTitle = (text) => {
                sheet.addRow([]);
                const row = sheet.addRow([text]);
                row.getCell(1).font = { bold: true, size: 12, color: { argb: argb(BLUE) } };
            };
            // backend percentages are 0–100; Excel percent format expects a fraction
            const pct = (v) => (Number(v) || 0) / 100;

            // title block
            sheet.addRow(['Movement Report']).getCell(1).font = { bold: true, size: 16, color: { argb: argb(NAVY) } };
            sheet.addRow(['Dublin Airport (DUB)']).getCell(1).font = { color: { argb: 'FF666666' } };
            sheet.addRow([`Period: ${periodLabel}`]).getCell(1).font = { color: { argb: 'FF666666' } };

            // donut — exceljs can't build native charts, so embed the rendered one as a picture
            // (the monthly file the client used to circulate was a PDF→Excel conversion that kept it)
            if (donutRef.current) {
                const donutCanvas = await html2canvas(donutRef.current, { scale: 2, logging: false, backgroundColor: '#ffffff' });
                const width = donutRef.current.offsetWidth;
                const height = donutRef.current.offsetHeight;
                const imageId = workbook.addImage({ base64: donutCanvas.toDataURL('image/png'), extension: 'png' });
                sheet.addRow([]);
                sheet.addImage(imageId, { tl: { col: 0, row: sheet.rowCount }, ext: { width, height } });
                // default row is 20px — reserve enough empty rows for the picture to sit above the tables
                for (let i = 0; i < Math.ceil(height / 20) + 1; i++) sheet.addRow([]);
            }

            // 1. pre-advised vs non-advised summary
            sectionTitle('Pre-Advised vs Non-Advised');
            const summaryHeader = sheet.addRow(['Direction', 'Total', 'Pre-Advised', 'Pre-Advised %', 'Non-Advised', 'Non-Advised %']);
            styleHeaderRow(summaryHeader, 1, 6);
            [
                { label: 'Outbound', d: report.outbound },
                { label: 'Inbound', d: report.inbound },
                { label: 'Total', d: report.total, isTotal: true },
            ].forEach(({ label, d, isTotal }) => {
                const row = sheet.addRow([
                    label, d?.total ?? 0,
                    d?.preAdvised ?? 0, pct(d?.preAdvisedPercentage),
                    d?.nonAdvised ?? 0, pct(d?.nonAdvisedPercentage),
                ]);
                styleBodyRow(row, 6, { isTotal, pctCols: [4, 6] });
            });

            // 2. assistance category breakdown
            sectionTitle('Assistance Category Breakdown');
            if (categoryTable && categoryTable.codes.length > 0) {
                const lastCol = categoryTable.codes.length + 2;
                const catHeader = sheet.addRow(['Category', ...categoryTable.codes, 'Total']);
                styleHeaderRow(catHeader, 1, lastCol);
                for (let c = 2; c < lastCol; c++) catHeader.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(BLUE) } };
                [
                    { label: 'Inbound', row: categoryTable.inboundRow },
                    { label: 'Outbound', row: categoryTable.outboundRow },
                    { label: 'Total', row: categoryTable.totalRow, isTotal: true },
                ].forEach(({ label, row, isTotal }) => {
                    const excelRow = sheet.addRow([label, ...row.cells, row.total]);
                    styleBodyRow(excelRow, lastCol, { isTotal });
                    excelRow.getCell(lastCol).font = { bold: true };
                });
            } else {
                sheet.addRow(['No assistance category data for this period.']);
            }

            // 3. terminal breakdown (two-row header, merged like the screen)
            sectionTitle('Terminal Breakdown (T1 / T2)');
            if (terminalRows) {
                const top = sheet.addRow(['Terminal', 'Inbound', '', 'Outbound', '', 'Total']);
                const sub = sheet.addRow(['', 'Pre-Advised', 'Non-Advised', 'Pre-Advised', 'Non-Advised', '']);
                styleHeaderRow(top, 1, 6, BLUE);
                styleHeaderRow(sub, 1, 6, BLUE);
                [1, 6].forEach((c) => {
                    top.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: argb(NAVY) } };
                    sheet.mergeCells(top.number, c, sub.number, c);
                });
                sheet.mergeCells(top.number, 2, top.number, 3);
                sheet.mergeCells(top.number, 4, top.number, 5);
                [
                    ...terminalRows.rows,
                    { label: 'Total', ...terminalRows.total, isTotal: true },
                ].forEach((r) => {
                    const row = sheet.addRow([
                        r.label,
                        r.inbound.preAdvised || 0, r.inbound.nonAdvised || 0,
                        r.outbound.preAdvised || 0, r.outbound.nonAdvised || 0,
                        r.total || 0,
                    ]);
                    styleBodyRow(row, 6, { isTotal: r.isTotal });
                    row.getCell(6).font = { bold: true };
                });
            } else {
                sheet.addRow(['No terminal breakdown available.']);
            }

            sheet.addRow([]);
            sheet.addRow([`PRM Assist  |  Generated ${moment().format('DD MMM YYYY HH:mm')}`]).getCell(1).font = { size: 9, color: { argb: 'FF999999' } };

            sheet.getColumn(1).width = 16;
            const widestTable = Math.max(6, (categoryTable?.codes.length || 0) + 2);
            for (let c = 2; c <= widestTable; c++) sheet.getColumn(c).width = 14;

            sanitizeExcelWorkbook(workbook);
            const buf = await workbook.xlsx.writeBuffer();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
            link.download = `Movement-Report-DUB-${data?.startDate}-to-${data?.endDate}.xlsx`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error('Movement report Excel generation failed:', err);
            alert('Failed to generate Excel. Please try again.');
        } finally {
            setDownloadingExcel(false);
        }
    };
    //#endregion

    //#region guards
    if (status === 'loading') {
        return <p style={{ color: '#6b7280', marginTop: '24px' }}>Generating Movement Report…</p>;
    }
    if (status === 'failed') {
        return <p style={{ color: '#C0392B', marginTop: '24px' }}>Error loading Movement Report: {error}</p>;
    }
    if (!report) {
        return (
            <p style={{ color: '#6b7280', marginTop: '24px' }}>
                Select a date range (max 1 month) and press <strong>Create Report</strong> to generate the Dublin Movement Report.
            </p>
        );
    }
    //#endregion

    const periodLabel = data?.startDate === data?.endDate
        ? moment(data?.startDate).format('Do MMMM YYYY')
        : `${moment(data?.startDate).format('Do MMM YYYY')} – ${moment(data?.endDate).format('Do MMM YYYY')}`;

    const dir = (bucket, key) => `${bucket?.[key] ?? 0}`;

    return (
        <div style={{ marginTop: '16px' }}>
            {/* actions — kept outside the printable area */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '12px' }}>
                <Button variant="outline-success" onClick={handleDownloadExcel} disabled={downloadingExcel}>
                    {downloadingExcel ? 'Preparing Excel…' : 'Download Excel'}
                </Button>
                <Button variant="outline-secondary" onClick={handleDownloadPdf} disabled={downloading}>
                    {downloading ? 'Preparing PDF…' : 'Download PDF'}
                </Button>
            </div>

            {/* printable report */}
            <div ref={reportRef} style={{ background: '#ffffff', padding: '24px', color: '#333' }}>
                {/* header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `3px solid ${NAVY}`, paddingBottom: '12px', marginBottom: '18px' }}>
                    <div>
                        <div style={{ fontSize: '22px', fontWeight: 700, color: NAVY }}>Movement Report</div>
                        <div style={{ fontSize: '14px', color: '#666', marginTop: '2px' }}>Dublin Airport (DUB)</div>
                        <div style={{ fontSize: '13px', color: '#666', marginTop: '2px' }}>Period: {periodLabel}</div>
                    </div>

                </div>

                {/* donut + summary side by side */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'center' }}>
                    <div ref={donutRef} style={{ flex: '0 0 240px', maxWidth: '260px', background: '#ffffff' }}>
                        <MovementPreadvisedPieChart
                            title="Pre-Advised vs Non-Advised"
                            preAdvised={report.total?.preAdvised ?? 0}
                            nonAdvised={report.total?.nonAdvised ?? 0}
                        />
                    </div>
                    <div style={{ flex: '1 1 380px', minWidth: '320px' }}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Direction</th>
                                    <th style={thStyle}>Total</th>
                                    <th style={thStyle}>Pre-Advised</th>
                                    <th style={thStyle}>Non-Advised</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { label: 'Outbound', d: report.outbound },
                                    { label: 'Inbound', d: report.inbound },
                                ].map(({ label, d }) => (
                                    <tr key={label}>
                                        <td style={labelTdStyle}>{label}</td>
                                        <td style={tdStyle}>{d?.total ?? 0}</td>
                                        <td style={tdStyle}>{d?.preAdvised ?? 0} ({d?.preAdvisedPercentage ?? 0}%)</td>
                                        <td style={tdStyle}>{d?.nonAdvised ?? 0} ({d?.nonAdvisedPercentage ?? 0}%)</td>
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={{ ...labelTdStyle, backgroundColor: 'transparent' }}>Total</td>
                                    <td style={tdStyle}>{report.total?.total ?? 0}</td>
                                    <td style={tdStyle}>{report.total?.preAdvised ?? 0} ({report.total?.preAdvisedPercentage ?? 0}%)</td>
                                    <td style={tdStyle}>{report.total?.nonAdvised ?? 0} ({report.total?.nonAdvisedPercentage ?? 0}%)</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* category breakdown */}
                <div style={sectionTitleStyle}>Assistance Category Breakdown</div>
                {categoryTable && categoryTable.codes.length > 0 ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle}>Category</th>
                                    {categoryTable.codes.map((c) => (
                                        <th key={c} style={subThStyle}>{c}</th>
                                    ))}
                                    <th style={thStyle}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    { label: 'Inbound', row: categoryTable.inboundRow },
                                    { label: 'Outbound', row: categoryTable.outboundRow },
                                ].map(({ label, row }) => (
                                    <tr key={label}>
                                        <td style={labelTdStyle}>{label}</td>
                                        {row.cells.map((v, i) => (
                                            <td key={i} style={tdStyle}>{v || ''}</td>
                                        ))}
                                        <td style={{ ...tdStyle, fontWeight: 700 }}>{row.total}</td>
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={{ ...labelTdStyle, backgroundColor: 'transparent' }}>Total</td>
                                    {categoryTable.totalRow.cells.map((v, i) => (
                                        <td key={i} style={tdStyle}>{v || ''}</td>
                                    ))}
                                    <td style={tdStyle}>{categoryTable.totalRow.total}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>No assistance category data for this period.</p>
                )}

                {/* terminal breakdown */}
                <div style={sectionTitleStyle}>Terminal Breakdown (T1 / T2)</div>
                {terminalRows ? (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={thStyle} rowSpan={2}>Terminal</th>
                                    <th style={subThStyle} colSpan={2}>Inbound</th>
                                    <th style={subThStyle} colSpan={2}>Outbound</th>
                                    <th style={thStyle} rowSpan={2}>Total</th>
                                </tr>
                                <tr>
                                    <th style={subThStyle}>Pre-Advised</th>
                                    <th style={subThStyle}>Non-Advised</th>
                                    <th style={subThStyle}>Pre-Advised</th>
                                    <th style={subThStyle}>Non-Advised</th>
                                </tr>
                            </thead>
                            <tbody>
                                {terminalRows.rows.map((r) => (
                                    <tr key={r.label}>
                                        <td style={labelTdStyle}>{r.label}</td>
                                        <td style={tdStyle}>{dir(r.inbound, 'preAdvised')}</td>
                                        <td style={tdStyle}>{dir(r.inbound, 'nonAdvised')}</td>
                                        <td style={tdStyle}>{dir(r.outbound, 'preAdvised')}</td>
                                        <td style={tdStyle}>{dir(r.outbound, 'nonAdvised')}</td>
                                        <td style={{ ...tdStyle, fontWeight: 700 }}>{r.total}</td>
                                    </tr>
                                ))}
                                <tr style={totalRowStyle}>
                                    <td style={{ ...labelTdStyle, backgroundColor: 'transparent' }}>Total</td>
                                    <td style={tdStyle}>{terminalRows.total.inbound.preAdvised}</td>
                                    <td style={tdStyle}>{terminalRows.total.inbound.nonAdvised}</td>
                                    <td style={tdStyle}>{terminalRows.total.outbound.preAdvised}</td>
                                    <td style={tdStyle}>{terminalRows.total.outbound.nonAdvised}</td>
                                    <td style={tdStyle}>{terminalRows.total.total}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p style={{ fontSize: '13px', color: '#6b7280' }}>No terminal breakdown available.</p>
                )}

                <div style={{ marginTop: '20px', fontSize: '11px', color: '#999', textAlign: 'center' }}>
                    PRM Assist  |  Generated {moment().format('DD MMM YYYY HH:mm')}
                </div>
            </div>
        </div>
    );
};

export default MovementReportPage;
